import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import { border1, green } from 'utils/style/colors';

interface IProgressProps {
  title?: string;
  percentage: number;
}

interface ICircleProgressProps {
  duration: number;
  size?: number;
  strokeWidth?: number;
  children: React.ReactNode;
  autoPlay?: boolean;
  strokeColor?: string;
}

interface IBarWrapperProps {
  withTitle: boolean;
}

const ProgressBody = styled.div`
  background-color: #d0d0d0;
  border-radius: 2em;
  width: 200px;
  height: 25px;
  display: flex;
  justify-content: flex-start;
`;
const ProgressTitle = styled.div`
  text-align: center;
  width: 20%;
  padding: 5px;
  color: var(--exact-white);
`;
const BarWrapper = styled('div')<IBarWrapperProps>`
  background-color: var(--exact-white);
  width: ${(props) => (props.withTitle ? '80%' : '100%')};
  height: 100%;
  padding: 5px;
  border-radius: 2em;
  border: ${border1};
`;

const Bar = styled('div')<IProgressProps>`
  background-color: ${green};
  border-radius: 2em;
  width: ${(props) => (props.percentage ? `${props.percentage}%` : '0%')};
  height: 100%;
`;

const Progress: React.FC<IProgressProps> = ({ title, percentage }) => {
  return (
    <ProgressBody>
      {title && <ProgressTitle>{title}</ProgressTitle>}
      <BarWrapper withTitle={!!title}>
        <Bar percentage={percentage} />
      </BarWrapper>
    </ProgressBody>
  );
};

export const CircleProgressBorder: React.FC<ICircleProgressProps> = ({
  duration,
  children,
  size = 25,
  autoPlay = false,
  strokeWidth = 2,
  strokeColor = 'black',
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const circleRef = useRef<SVGCircleElement>(null);
  const animationId = useRef<number | null>(null);

  const animate = () => {
    const circle = circleRef.current;
    if (!circle) return;

    circle.style.transition = 'none';
    circle.style.strokeDashoffset = `${circumference}`;
    circle.getBoundingClientRect();

    if (autoPlay) {
      circle.style.transition = `stroke-dashoffset ${duration}s linear`;
      circle.style.strokeDashoffset = '0';
    }
  };

  const loopAnimation = () => {
    animate();

    animationId.current = window.setTimeout(() => {
      loopAnimation();
    }, duration * 1000);
  };

  useEffect(() => {
    const circle = circleRef.current;
    if (!circle) return;

    circle.style.strokeDasharray = `${circumference}`;
    circle.style.strokeDashoffset = `${circumference}`;

    if (autoPlay) {
      loopAnimation();
    } else {
      animate();
    }

    return () => {
      if (animationId.current) {
        clearTimeout(animationId.current);
      }
    };
  }, [duration, autoPlay, circumference]);

  return (
    <div
      className="relative flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg className="absolute top-0 left-0" width={size} height={size}>
        <circle
          ref={circleRef}
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="transparent"
          stroke={`${strokeColor}`}
          strokeWidth={strokeWidth}
        />
      </svg>
      <div className="z-10">{children}</div>
    </div>
  );
};

export default React.memo(Progress);
